import { Suspense, useEffect, useRef, useState } from 'react';
import { QuietErrorBoundary } from '@gonfalon/error-boundaries/QuietErrorBoundary';
import { msg } from '@gonfalon/messages/msg';
import { Box } from '@launchpad-ui/box';
import { Button, Dialog, Heading, IconButton, Popover, type PopoverProps, Text } from '@launchpad-ui/components';

import { type AnnouncementKey, getAnnouncement } from './availableAnnouncements';
import {
  trackAnnouncementCommandCopied,
  trackAnnouncementCtaClicked,
  trackAnnouncementShown,
} from './internal/analytics';
import { useOneTimeAnnouncement } from './useOneTimeAnnouncement';

import styles from './AnnouncementPopover.module.css';

type AnnouncementPopoverProps = {
  /** Key of the announcement to render (looked up in the config catalog). */
  announcementKey: AnnouncementKey;
  /** Popover placement relative to the anchor. */
  placement?: PopoverProps['placement'];
};

/**
 * Renders a one-time, dismissible announcement as a popover, driven by the config
 * catalog. Shows at most once per member: it auto-opens when the announcement is
 * enabled and the member hasn't seen it, and marks it seen on show (server-persisted).
 *
 * The popover anchors to an inline, visually-hidden marker, so mount this component
 * wherever you want the popover to appear (e.g. next to the profile menu in the chrome).
 *
 * The exported `AnnouncementPopover` suspends while the dismissals load and wraps this in
 * a `QuietErrorBoundary`, so a failed fetch (or any render error) renders nothing and
 * never affects the app.
 */
function AnnouncementPopoverInner({ announcementKey, placement = 'bottom start' }: AnnouncementPopoverProps) {
  const announcement = getAnnouncement(announcementKey);
  const { dismissed, markSeen } = useOneTimeAnnouncement(announcementKey);

  const anchorRef = useRef<HTMLSpanElement>(null);
  // The dismissals query has already resolved (via Suspense), so we can decide whether to
  // auto-open synchronously on the first render — no effect needed to react to the load.
  const [isOpen, setIsOpen] = useState(Boolean(announcement?.enabled) && !dismissed);

  // The only side effects: persist the "seen" record (server-side, enforcing the one-time
  // cap) and fire analytics, once, when the popover auto-opens. Display is then driven by
  // local `isOpen`, so the popover isn't yanked away when `markSeen` updates the cache.
  useEffect(() => {
    if (isOpen) {
      markSeen();
      trackAnnouncementShown(announcementKey);
    }
    // Mount-only: this is the auto-open side effect, not change detection.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!announcement || !isOpen) {
    return null;
  }

  const handleCopyCommand = () => {
    if (announcement.command) {
      void navigator.clipboard?.writeText(announcement.command);
      trackAnnouncementCommandCopied(announcementKey);
    }
  };

  return (
    <>
      <span ref={anchorRef} className={styles.anchor} aria-hidden="true" />
      <Popover
        triggerRef={anchorRef}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        placement={placement}
        offset={8}
        className={styles.popover}
        aria-label={announcement.title}
      >
        <Dialog className={styles.dialog} aria-label={announcement.title}>
          {() => (
            <Box display="flex" flexDirection="column" gap="$300">
              <div slot="header" className={styles.dismissRow}>
                <IconButton
                  slot="close"
                  aria-label={msg('announcements.announcementPopover.dismissLabel')}
                  icon="cancel"
                  size="small"
                  variant="minimal"
                  onPress={() => {
                    markSeen();
                    setIsOpen(false);
                  }}
                />
              </div>
              <Heading slot="title" size="small" className={styles.title}>
                {announcement.title}
              </Heading>
              <Text size="medium" slot="subtitle" className={styles.description}>
                {announcement.description}
              </Text>
              {announcement.command ? (
                <div className={styles.command}>
                  <code className={styles.commandText}>{announcement.command}</code>
                  <Button size="small" variant="minimal" onPress={handleCopyCommand}>
                    {msg('announcements.announcementPopover.copyCommand')}
                  </Button>
                </div>
              ) : null}
              <div className={styles.footer}>
                <Button
                  variant="primary"
                  onPress={() => {
                    trackAnnouncementCtaClicked(announcementKey);
                    markSeen();
                    window.open(announcement.ctaLink, '_blank', 'noopener,noreferrer');
                    setIsOpen(false);
                  }}
                >
                  {announcement.ctaLabel}
                </Button>
              </div>
            </Box>
          )}
        </Dialog>
      </Popover>
    </>
  );
}

export function AnnouncementPopover(props: AnnouncementPopoverProps) {
  return (
    <QuietErrorBoundary>
      <Suspense fallback={null}>
        <AnnouncementPopoverInner {...props} />
      </Suspense>
    </QuietErrorBoundary>
  );
}
